import { ComponentProps, ReactNode, useState } from 'react';

import Card from '~/shared/components/Card';
import { Volume } from '~/shared/types/api';
import Tag from '~/shared/components/Tag';
import Typography from '~/shared/components/Typography';
import Button from '~/shared/components/Buttons';
import DeleteIcon from '~/shared/assets/icons/delete.svg';

import OptimisticSwitch from '../FleetManagerPage/OptimisticSwitch';
import { OptimisticSwitchType } from '../FleetManagerPage/helpers';
import {
  statusToTagVariantMap,
  getProvisionedUtilization,
  getDatafyUtilization,
} from '../common/helpers';

import DeleteModal from './DeleteModal';

export default function GeneralInfo({ volume }: { volume: Volume }) {
  const [optimisticVolumeSwitches, setOptimisticVolumeSwitches] = useState<OptimisticSwitchType>(
    []
  );
  const [isModalOpen, setIsModalOpen] = useState(false);

  return (
    <Card>
      <div className="h-[48px] flex justify-between items-center mb-[20px]">
        <Typography variant="H5">General Info</Typography>

        <div className="gap-[35px] items-center flex">
          {volume.monitoring === 'Unattached' ? (
            <Button variant="secondary" onClick={() => setIsModalOpen(true)}>
              <DeleteIcon className="w-6 aspect-square fill-primary-400" />
              <Typography variant="buttons-1" color="text-primary-500">
                Delete
              </Typography>
            </Button>
          ) : null}

          <OptimisticSwitch
            volume={volume}
            optimisticVolumeSwitches={optimisticVolumeSwitches}
            setOptimisticVolumeSwitches={setOptimisticVolumeSwitches}
            showLabel
          />
        </div>
      </div>

      <div className="grid grid-cols-3 gap-x-[6px] gap-y-[30px]">
        {getInfoItems(volume).map((item) => (
          <InfoItem key={item.title} {...item} />
        ))}
      </div>

      <DeleteModal showModal={isModalOpen} volume={volume} onClose={() => setIsModalOpen(false)} />
    </Card>
  );
}

function InfoItem({ title, value }: { title: string; value: ReactNode }) {
  return (
    <div className="px-[8px] pt-[10px] pb-[4px] flex flex-col w-full justify-between h-[70px] max-w-full">
      <Typography variant="p3" truncate title={title}>
        {title}
      </Typography>
      {typeof value === 'string' || typeof value === 'number' ? <Value value={value} /> : value}
    </div>
  );
}

function Value({
  variant = 'default',
  value,
}: {
  variant?: 'default' | 'green';
  value?: string | number;
}) {
  return (
    <Typography
      variant="p2"
      color={
        !value || value === 'N/A'
          ? 'text-grey-1'
          : variant === 'green'
            ? 'text-lime-green'
            : undefined
      }
      truncate
      title={value?.toString()}
    >
      {value || 'N/A'}
    </Typography>
  );
}

function getInfoItems(volume: Volume): ComponentProps<typeof InfoItem>[] {
  const {
    instanceId,
    monitoring,
    yearlyEstimatedSavings,
    yearlyRealizedSavings,
    diskType,
    iops,
    throughput,
    awsState,
    awsStatus,
    availabilityZone,
    region,
    clusterName,
    nodeGroupName,
    osImage,
    isBootDisk,
    statusProgress,
  } = volume;

  const progress = monitoring.endsWith('ing') ? statusProgress : undefined;
  return [
    {
      title: 'Instance ID',
      value: instanceId,
    },
    {
      title: 'Datafy Status',
      value: (
        <Tag text={monitoring} variant={statusToTagVariantMap[monitoring]} progress={progress} />
      ),
    },
    {
      title: 'Provisioned size (utilization)',
      value: getProvisionedUtilization(volume),
    },
    {
      title: 'Datafy size (utilization)',
      value: getDatafyUtilization(volume),
    },
    {
      title: 'Yearly Estimated Savings',
      value: yearlyEstimatedSavings,
    },
    {
      title: 'Yearly Realized Savings',
      value: <Value value={yearlyRealizedSavings} variant="green" />,
    },
    {
      title: 'Type',
      value: diskType,
    },
    {
      title: 'IOPS',
      value: iops,
    },
    {
      title: 'Throughput',
      value: throughput,
    },
    {
      title: 'AWS State',
      value: awsState,
    },
    {
      title: 'AWS Status',
      value: awsStatus,
    },
    {
      title: 'Availability zone',
      value: availabilityZone,
    },
    {
      title: 'Region',
      value: region,
    },
    {
      title: 'Cluster',
      value: clusterName,
    },
    {
      title: 'Node-group',
      value: nodeGroupName,
    },
    {
      title: 'OS',
      value: osImage,
    },
    {
      title: 'Boot Volume',
      value: isBootDisk ? 'Y' : 'N',
    },
  ];
}
